import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import './Landing.css';

const Landing = () => {
  const navigate = useNavigate();

  // FAQ state
  const [openFaq, setOpenFaq] = useState(0); // first one open by default

  // Signup modal state
  const [showSignup, setShowSignup] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('Plus');
  const [signupStep, setSignupStep] = useState('form');
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', password2: ''
  });
  const [kids, setKids] = useState([{ id: 1, kid_name: '', kid_username: '' }]);
  const [kidCounter, setKidCounter] = useState(1);
  const [errors, setErrors] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [usernameStatuses, setUsernameStatuses] = useState({});
  const usernameTimers = useRef({});

  // Scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add('animate');
      });
    }, { threshold: 0.1 });
    document.querySelectorAll('.setup-card, .review-card, .price-card, .proof-item').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Feature card hover states
  const featureCards = [
    {
      title: 'Screenshots',
      desc: 'Auto-capture every 5-30 minutes. Private gallery with timestamps. Download or delete anytime.',
      color: '#2563eb', bg: 'linear-gradient(135deg,#dbeafe,#bfdbfe)', hoverBorder: '#bfdbfe', hoverShadow: '0 12px 40px rgba(59,130,246,0.08)',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
    },
    {
      title: 'Website Tracking',
      desc: 'Every site across Chrome, Safari, Firefox, Brave & Edge. Harmful content flagged instantly.',
      color: '#16a34a', bg: 'linear-gradient(135deg,#dcfce7,#bbf7d0)', hoverBorder: '#bbf7d0', hoverShadow: '0 12px 40px rgba(34,197,94,0.08)',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
    },
    {
      title: 'Smart Alerts',
      desc: '50+ harmful keywords built-in. Custom watchlist. Instant alerts via dashboard or WhatsApp.',
      color: '#dc2626', bg: 'linear-gradient(135deg,#fee2e2,#fecaca)', hoverBorder: '#fecaca', hoverShadow: '0 12px 40px rgba(239,68,68,0.08)',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
    },
    {
      title: 'Screen Time',
      desc: 'Daily limits from 30 min to 8 hours. Warnings at 80%. Alert when limit is reached.',
      color: '#7c3aed', bg: 'linear-gradient(135deg,#f3e8ff,#e9d5ff)', hoverBorder: '#e9d5ff', hoverShadow: '0 12px 40px rgba(168,85,247,0.08)',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
    },
    {
      title: 'Bedtime Control',
      desc: 'Set bedtime schedule. Instant alert when computer is used during sleeping hours.',
      color: '#d97706', bg: 'linear-gradient(135deg,#fef3c7,#fde68a)', hoverBorder: '#fde68a', hoverShadow: '0 12px 40px rgba(245,158,11,0.08)',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
    },
    {
      title: 'App Tracking',
      desc: 'See which apps are used and for how long. Charts and breakdowns. Spot overuse instantly.',
      color: '#0891b2', bg: 'linear-gradient(135deg,#cffafe,#a5f3fc)', hoverBorder: '#a5f3fc', hoverShadow: '0 12px 40px rgba(6,182,212,0.08)',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="#0891b2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
    },
    {
      title: 'Stealth Mode',
      desc: 'Completely invisible. No icon, no trace. Password-protected — only you can uninstall.',
      color: '#2563eb', bg: 'linear-gradient(135deg,#dbeafe,#bfdbfe)', hoverBorder: '#bfdbfe', hoverShadow: '0 12px 40px rgba(59,130,246,0.08)',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
    },
    {
      title: 'Block Websites',
      desc: 'Block dangerous sites. Get instant alerts when your child tries to visit them.',
      color: '#dc2626', bg: 'linear-gradient(135deg,#fee2e2,#fecaca)', hoverBorder: '#fecaca', hoverShadow: '0 12px 40px rgba(239,68,68,0.08)',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
    },
    {
      title: 'Weekly Reports',
      desc: 'Beautiful weekly summaries with charts, top apps, top sites, and activity trends.',
      color: '#16a34a', bg: 'linear-gradient(135deg,#dcfce7,#bbf7d0)', hoverBorder: '#bbf7d0', hoverShadow: '0 12px 40px rgba(34,197,94,0.08)',
      icon: <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
    },
  ];

  const [hoveredFeature, setHoveredFeature] = useState(null);

  // ── Signup helpers ──

  const openSignup = (plan = 'Plus') => {
    setSelectedPlan(plan);
    setSignupStep('form');
    setFormData({ name: '', email: '', phone: '', password: '', password2: '' });
    setKids([{ id: 1, kid_name: '', kid_username: '' }]);
    setKidCounter(1);
    setErrors('');
    setUsernameStatuses({});
    setShowSignup(true);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addKid = () => {
    const newId = kidCounter + 1;
    setKidCounter(newId);
    setKids(prev => [...prev, { id: newId, kid_name: '', kid_username: '' }]);
  };

  const removeKid = (id) => {
    if (kids.length <= 1) return;
    setKids(prev => prev.filter(k => k.id !== id));
    setUsernameStatuses(prev => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const updateKid = (id, field, value) => {
    setKids(prev => prev.map(k => k.id === id ? { ...k, [field]: value } : k));
    if (field === 'kid_username') {
      checkUsernameAvailability(id, value);
    }
  };

  const checkUsernameAvailability = useCallback((kidId, username) => {
    if (usernameTimers.current[kidId]) clearTimeout(usernameTimers.current[kidId]);
    if (!username || username.length < 3) {
      setUsernameStatuses(prev => ({ ...prev, [kidId]: null }));
      return;
    }
    setUsernameStatuses(prev => ({ ...prev, [kidId]: 'checking' }));
    usernameTimers.current[kidId] = setTimeout(async () => {
      try {
        const { data, error } = await supabase.rpc('kid_username_exists', { p_username: username });
        if (error) {
          setUsernameStatuses(prev => ({ ...prev, [kidId]: 'error' }));
        } else if (data === true) {
          setUsernameStatuses(prev => ({ ...prev, [kidId]: 'taken' }));
        } else {
          setUsernameStatuses(prev => ({ ...prev, [kidId]: 'available' }));
        }
      } catch {
        setUsernameStatuses(prev => ({ ...prev, [kidId]: 'error' }));
      }
    }, 500);
  }, []);

  const handleSignupSubmit = async () => {
    setErrors('');
    // Validate
    if (!formData.name.trim()) return setErrors('Name is required.');
    if (!formData.email.trim()) return setErrors('Email is required.');
    if (!formData.password || formData.password.length < 6) return setErrors('Password must be at least 6 characters.');
    if (formData.password !== formData.password2) return setErrors('Passwords do not match.');
    for (const kid of kids) {
      if (!kid.kid_name.trim()) return setErrors('Every child must have a name.');
      if (!kid.kid_username.trim() || kid.kid_username.length < 3) return setErrors('Every child must have a username (min 3 chars).');
      if (usernameStatuses[kid.id] === 'taken') return setErrors(`Username @${kid.kid_username} is already taken.`);
    }
    setSubmitting(true);
    try {
      // 1. Sign up (with retry for lock errors)
      let authData, attempt = 0;
      // Check if email already exists (via RPC — doesn't leak customer data)
      const { data: emailExists } = await supabase.rpc('customer_exists_by_email', {
        p_email: formData.email.trim().toLowerCase(),
      });
      if (emailExists === true) {
        throw new Error('An account with this email already exists. Please sign in instead.');
      }

      // Check if phone already exists (if provided)
      if (formData.phone?.trim()) {
        const cleanPhone = formData.phone.trim().replace(/\s+/g, '');
        const { data: phoneExists } = await supabase.rpc('customer_exists_by_phone', {
          p_phone: cleanPhone,
        });
        if (phoneExists === true) {
          throw new Error('An account with this phone number already exists. Please sign in instead.');
        }
      }

      while (attempt < 3) {
        try {
          const result = await supabase.auth.signUp({
            email: formData.email,
            password: formData.password,
          });
          if (result.error) throw result.error;
          authData = result.data;
          break;
        } catch (e) {
          if (e.message?.includes('Lock') && attempt < 2) {
            attempt++;
            await new Promise(r => setTimeout(r, 500 * attempt));
            continue;
          }
          throw e;
        }
      }
      const userId = authData?.user?.id;
      // Supabase returns a user with identities=[] if email already exists in auth
      if (!userId || (authData.user?.identities && authData.user.identities.length === 0)) {
        throw new Error('An account with this email already exists. Please sign in or use "Forgot Password".');
      }

      // 2. Insert customer and get back the customer row ID
      const planMap = { 'Free': 'free', 'Plus': 'plus', 'Family': 'family' };
      const { data: custResult, error: custError } = await supabase.rpc('create_customer_on_signup', {
        p_auth_id: userId,
        p_name: formData.name,
        p_email: formData.email.trim().toLowerCase(),
        p_phone: formData.phone?.trim().replace(/\s+/g, '') || null,
        p_plan: planMap[selectedPlan] || 'free',
      });
      if (custError) throw custError;
      const customerId = custResult?.id;

      // 3. Insert kids (kids table uses customer_id, not parent_id)
      for (const kid of kids) {
        const { error: kidError } = await supabase.from('kids').insert({
          customer_id: customerId,
          kid_name: kid.kid_name,
          kid_username: kid.kid_username,
        });
        if (kidError) throw kidError;
      }

      // 4. Insert default settings
      const { error: settingsError } = await supabase.from('settings').insert({
        user_id: userId,
        parent_id: userId,
        screenshot_interval: 10,
        screen_time_limit: 180,
        bedtime_start: '22:00',
        bedtime_end: '07:00',
      });
      if (settingsError) console.warn('Settings insert warning:', settingsError);

      setSignupStep('success');
    } catch (err) {
      setErrors(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // FAQ data
  const faqItems = [
    { q: 'Will my child know they\'re being monitored?', a: 'Free plan shows a small shield icon. Paid plans offer Stealth Mode \u2014 completely invisible. No icon, no notification, no way for your child to know.' },
    { q: 'What happens when my trial ends?', a: 'You automatically switch to the Free plan. No charges, no credit card. Basic monitoring continues forever at zero cost.' },
    { q: 'Is my child\'s data private and secure?', a: 'Yes. All data is encrypted and stored securely. Only you can see your child\'s activity through your password-protected dashboard. We never sell or share data.' },
    { q: 'Can my child uninstall the app?', a: 'No. Uninstalling requires the parent password you set during installation. Without it, the app cannot be removed.' },
    { q: 'Which devices are supported?', a: 'Currently macOS only \u2014 MacBook Air, MacBook Pro, iMac, Mac Mini (Apple Silicon and Intel). Windows and mobile versions coming soon.' },
    { q: 'Can I monitor more than one child?', a: 'Free and Plus plans support 1 child. Family plan supports up to 3 children, each with their own profile and unique @username.' },
  ];

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f172a', background: '#fff', overflowX: 'hidden' }}>

      {/* ═══ NAV ═══ */}
      <nav className="landing-nav">
        <div className="nav-inner">
          <a href="#" className="nav-logo" onClick={e => e.preventDefault()}>
            <div className="shield">{'\u{1F6E1}'}</div> ParentsProtection
          </a>
          <div className="nav-center">
            <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>Features</a>
            <a href="#how" onClick={(e) => { e.preventDefault(); scrollToSection('how'); }}>How it works</a>
            <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}>Pricing</a>
            <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}>FAQ</a>
          </div>
          <div className="nav-right">
            <button className="btn-ghost" onClick={() => navigate('/login')}>Log in</button>
            <button className="btn-nav" onClick={() => openSignup('Plus')}>Get Started</button>
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <div>
              <div className="hero-tag"><div className="dot"></div> Trusted by parents across India</div>
              <h1>Your child's online world, <span className="gradient">made visible</span></h1>
              <p className="hero-desc">Silent screenshots, real-time alerts, screen time control. Know what your child does on their Mac — without them knowing.</p>
              <div className="hero-actions">
                <button className="btn-hero btn-hero-primary" onClick={() => openSignup('Plus')}>
                  Start Free Trial <span style={{ fontSize: '18px' }}>{'\u2192'}</span>
                </button>
                <button className="btn-hero btn-hero-secondary" onClick={() => scrollToSection('features')}>See Features</button>
              </div>
              <div className="hero-proof">
                <span><span className="check">{'\u2713'}</span> 7-day free trial</span>
                <span><span className="check">{'\u2713'}</span> 5 min setup</span>
                <span><span className="check">{'\u2713'}</span> No credit card needed</span>
              </div>
            </div>
            <div>
              <div className="mac">
                <div className="mac-chrome">
                  <div className="dot" style={{ background: '#ff5f57' }}></div>
                  <div className="dot" style={{ background: '#febc2e' }}></div>
                  <div className="dot" style={{ background: '#28c840' }}></div>
                  <div className="url">{'\uD83D\uDD12'} parentsprotection.org/dashboard</div>
                </div>
                <div className="mac-screen">
                  <div className="mac-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="mc">
                      <div className="mc-label">Screen Time Today</div>
                      <div className="mc-value">2h 45m</div>
                      <div className="mc-sub">/ 3h daily limit</div>
                      <div className="mc-bar"><div className="mc-fill" style={{ width: '75%', background: 'linear-gradient(90deg,#22c55e,#eab308)' }}></div></div>
                    </div>
                    <div className="mc">
                      <div className="mc-label">Active Alerts</div>
                      <div className="mc-value" style={{ color: '#ef4444' }}>3</div>
                      <div className="mc-sub">{'\uD83D\uDD34'} 1 urgent {'\u00B7'} {'\u26A0\uFE0F'} 2 warnings</div>
                    </div>
                  </div>
                  <div className="mac-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: '10px' }}>
                    <div className="mc">
                      <div className="mc-label">Active App</div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff', marginTop: '4px' }}>{'\uD83C\uDF10'} Chrome</div>
                      <div style={{ fontSize: '10px', color: '#22c55e', marginTop: '3px' }}>{'\u25CF'} Active right now</div>
                    </div>
                    <div className="mc">
                      <div className="mc-label">Websites Today</div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff', marginTop: '4px' }}>24 visited</div>
                      <div style={{ fontSize: '10px', color: '#f59e0b', marginTop: '3px' }}>{'\u26A0'} 2 flagged</div>
                    </div>
                  </div>
                  <div className="mac-grid" style={{ marginTop: '10px' }}>
                    <div className="mc" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <div style={{ width: '72px', height: '50px', background: 'linear-gradient(135deg,#1e293b,#334155)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>{'\uD83D\uDCF8'}</div>
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#fff' }}>Latest Screenshot</div>
                        <div style={{ fontSize: '9px', color: '#64748b', marginTop: '2px' }}>2 min ago {'\u00B7'} {'\uD83D\uDD12'} Private {'\u00B7'} Encrypted</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF NUMBERS ═══ */}
      <section className="social-proof">
        <div className="container">
          <div className="proof-grid">
            <div className="proof-item"><div className="proof-num">500<span>+</span></div><div className="proof-label">Parents trust us</div></div>
            <div className="proof-item"><div className="proof-num">10<span>M+</span></div><div className="proof-label">Screenshots captured</div></div>
            <div className="proof-item"><div className="proof-num">24<span>/7</span></div><div className="proof-label">Always monitoring</div></div>
            <div className="proof-item"><div className="proof-num">4.9<span>{'\u2605'}</span></div><div className="proof-label">Parent satisfaction</div></div>
            <div className="proof-item"><div className="proof-num">5<span>min</span></div><div className="proof-label">Setup time</div></div>
          </div>
        </div>
      </section>

      {/* ═══ FEATURES 3x3 ═══ */}
      <section id="features" className="bento">
        <div className="container">
          <div className="bento-header">
            <div className="tag-pill">Features</div>
            <div className="bento-title">Everything to protect them</div>
            <div className="bento-desc">Powerful, silent monitoring that gives you complete visibility into your child's digital life.</div>
          </div>
          <div className="features-grid">
            {featureCards.map((card, i) => (
              <div
                key={i}
                className="feature-card"
                style={{
                  borderColor: hoveredFeature === i ? card.hoverBorder : '#f1f5f9',
                  boxShadow: hoveredFeature === i ? card.hoverShadow : 'none',
                  transform: hoveredFeature === i ? 'translateY(-3px)' : 'none',
                }}
                onMouseEnter={() => setHoveredFeature(i)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                <div className="feature-icon-wrap" style={{ background: card.bg }}>
                  {card.icon}
                </div>
                <h3>{card.title}</h3>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ DEEP DIVE: Screenshots ═══ */}
      <section className="deep">
        <div className="container">
          <div className="deep-inner">
            <div className="deep-content">
              <div className="pill" style={{ background: '#eff6ff', color: '#2563eb' }}>Screenshots</div>
              <h2>See their screen, anytime</h2>
              <p>Automatic captures give you a real-time window into your child's computer activity. Every screenshot is encrypted and only visible to you.</p>
              <div className="deep-checks">
                <div className="deep-check">Every 5, 10, 15 or 30 min</div>
                <div className="deep-check">End-to-end encrypted</div>
                <div className="deep-check">Full-size zoom view</div>
                <div className="deep-check">Download or delete</div>
              </div>
              <button className="btn-feat" onClick={() => openSignup('Plus')}>Try It Free {'\u2192'}</button>
            </div>
            <div className="deep-visual">
              <div className="vis-card">
                <div className="vis-header">
                  <div className="vis-dot" style={{ background: '#ff5f57' }}></div>
                  <div className="vis-dot" style={{ background: '#febc2e' }}></div>
                  <div className="vis-dot" style={{ background: '#28c840' }}></div>
                </div>
                <div className="vis-body" style={{ background: '#f8fafc' }}>{'\uD83D\uDCF8'}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ DEEP DIVE: Protection ═══ */}
      <section className="deep reverse">
        <div className="container">
          <div className="deep-inner">
            <div className="deep-content">
              <div className="pill" style={{ background: '#fef2f2', color: '#dc2626' }}>Protection</div>
              <h2>Instant alerts for harmful content</h2>
              <p>Our built-in detection catches drugs, gambling, adult content, and more. Add your own keywords to watch for. Get notified via dashboard or WhatsApp.</p>
              <div className="deep-checks">
                <div className="deep-check">50+ harmful keywords built-in</div>
                <div className="deep-check">Custom keyword watchlist</div>
                <div className="deep-check">Block specific websites</div>
                <div className="deep-check">WhatsApp instant alerts</div>
              </div>
              <button className="btn-feat" onClick={() => openSignup('Plus')}>Try It Free {'\u2192'}</button>
            </div>
            <div className="deep-visual">
              <div className="vis-card">
                <div className="vis-header">
                  <div className="vis-dot" style={{ background: '#ff5f57' }}></div>
                  <div className="vis-dot" style={{ background: '#febc2e' }}></div>
                  <div className="vis-dot" style={{ background: '#28c840' }}></div>
                </div>
                <div className="vis-body" style={{ background: '#fef2f2' }}>{'\uD83D\uDEA8'}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SETUP STEPS ═══ */}
      <section id="how" className="setup">
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <div className="tag-pill" style={{ background: 'rgba(14,165,233,0.1)', borderColor: 'rgba(14,165,233,0.2)', color: '#38bdf8' }}>How it works</div>
            <div className="bento-title" style={{ color: '#fff' }}>Ready in 5 minutes</div>
            <div className="bento-desc" style={{ margin: '0 auto', color: '#64748b' }}>No technical skills needed. Just sign up, install, and start watching.</div>
          </div>
          <div className="setup-grid">
            <div className="setup-card">
              <div className="setup-num">1</div>
              <h4>Create Your Account</h4>
              <p>Sign up free with email. Add your children — each gets a unique @username like Instagram.</p>
              <div className="setup-connector">{'\u2192'}</div>
            </div>
            <div className="setup-card">
              <div className="setup-num">2</div>
              <h4>Install on Their Mac</h4>
              <p>Download the app, install on your child's Mac. Enter the 6-digit code from your dashboard to link it.</p>
              <div className="setup-connector">{'\u2192'}</div>
            </div>
            <div className="setup-card">
              <div className="setup-num">3</div>
              <h4>Start Monitoring</h4>
              <p>Open your dashboard on any device. Screenshots, websites, alerts — everything appears within minutes.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ COMPARISON ═══ */}
      <section className="compare">
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <div className="tag-pill">Compare</div>
            <div className="bento-title">Why parents switch to us</div>
          </div>
          <table className="comp-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>ParentsProtection</th>
                <th>Qustodio</th>
                <th>Net Nanny</th>
                <th>Bark</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={{ fontWeight: 600 }}>Screenshot capture</td><td className="y">{'\u2705'} Every 5 min</td><td className="n">{'\u274C'}</td><td className="n">{'\u274C'}</td><td className="n">{'\u274C'}</td></tr>
              <tr><td>All browser tracking</td><td className="y">{'\u2705'} 5 browsers</td><td>Partial</td><td>Partial</td><td className="n">{'\u274C'}</td></tr>
              <tr><td>Stealth mode</td><td className="y">{'\u2705'} Invisible</td><td className="n">{'\u274C'}</td><td className="n">{'\u274C'}</td><td className="n">{'\u274C'}</td></tr>
              <tr><td>Screen time limits</td><td className="y">{'\u2705'}</td><td className="y">{'\u2705'}</td><td className="y">{'\u2705'}</td><td className="n">{'\u274C'}</td></tr>
              <tr><td>WhatsApp alerts</td><td className="y">{'\u2705'}</td><td className="n">{'\u274C'}</td><td className="n">{'\u274C'}</td><td className="n">{'\u274C'}</td></tr>
              <tr><td>macOS native</td><td className="y">{'\u2705'} Optimized</td><td>Partial</td><td>Partial</td><td>Partial</td></tr>
              <tr><td>Indian keywords</td><td className="y">{'\u2705'} Hindi + English</td><td className="n">{'\u274C'}</td><td className="n">{'\u274C'}</td><td className="n">{'\u274C'}</td></tr>
              <tr><td>Free plan</td><td className="y">{'\u2705'} Forever</td><td className="n">{'\u274C'}</td><td className="n">{'\u274C'}</td><td>Limited</td></tr>
              <tr><td style={{ fontWeight: 800 }}>Monthly price</td><td className="y" style={{ fontWeight: 900, fontSize: '16px' }}>{'\u20B9'}499</td><td>{'\u20B9'}800+</td><td>{'\u20B9'}750+</td><td>{'\u20B9'}900+</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="reviews">
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <div className="tag-pill">Reviews</div>
            <div className="bento-title">Parents love us</div>
          </div>
          <div className="reviews-grid">
            <div className="review-card">
              <div className="review-stars">{'★★★★★'.split('').map((s, i) => <span key={i} className="star">{s}</span>)}</div>
              <div className="review-text">"Caught my son on a phishing site in the first week. The screenshot feature alone is worth every rupee. This is a must-have for every Indian parent."</div>
              <div className="review-author">
                <div className="review-avatar">{'\uD83D\uDC69'}</div>
                <div className="review-meta"><div className="name">Priya Sharma</div><div className="role">Mother of 2 {'\u00B7'} Mumbai</div></div>
              </div>
            </div>
            <div className="review-card">
              <div className="review-stars">{'★★★★★'.split('').map((s, i) => <span key={i} className="star">{s}</span>)}</div>
              <div className="review-text">"3-minute setup, stealth mode so my daughter doesn't know. The weekly reports give me complete peace of mind. Brilliant product."</div>
              <div className="review-author">
                <div className="review-avatar">{'\uD83D\uDC68'}</div>
                <div className="review-meta"><div className="name">Rajesh Kumar</div><div className="role">Father {'\u00B7'} Bangalore</div></div>
              </div>
            </div>
            <div className="review-card">
              <div className="review-stars">{'★★★★★'.split('').map((s, i) => <span key={i} className="star">{s}</span>)}</div>
              <div className="review-text">"Bedtime alerts solved everything. No more arguments about late-night computer use. My kids' sleep improved within a week."</div>
              <div className="review-author">
                <div className="review-avatar">{'\uD83D\uDC69'}</div>
                <div className="review-meta"><div className="name">Anita Mehta</div><div className="role">Mother of 3 {'\u00B7'} Delhi</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" className="pricing">
        <div className="container">
          <div style={{ textAlign: 'center' }}>
            <div className="tag-pill">Pricing</div>
            <div className="bento-title">Choose your plan</div>
            <div className="bento-desc" style={{ margin: '0 auto' }}>Every plan starts with a 7-day free trial. No credit card required.</div>
          </div>
          <div className="pricing-toggle">
            <span>Monthly</span>
            <span style={{ color: '#d1d5db' }}>|</span>
            <span>Annual</span>
            <span className="save">Save 30%</span>
          </div>
          <div className="price-grid">
            {/* Free */}
            <div className="price-card">
              <h3>Free Forever</h3>
              <div className="price-amount">{'\u20B9'}0</div>
              <div className="price-period">forever — no charge, ever</div>
              <ul className="price-list">
                <li>{'\u2705'} Screenshots every 30 min</li>
                <li>{'\u2705'} 20 website URLs tracked</li>
                <li>{'\u2705'} App usage tracking</li>
                <li>{'\u2705'} Content alerts</li>
                <li>{'\u2705'} 1 child</li>
                <li className="off">— Screen time limits</li>
                <li className="off">— Bedtime control</li>
                <li className="off">— Stealth mode</li>
              </ul>
              <button className="price-btn price-btn-outline" onClick={() => openSignup('Free')}>Get Started Free</button>
            </div>
            {/* Plus */}
            <div className="price-card featured">
              <div className="price-badge">MOST POPULAR</div>
              <h3>Plus</h3>
              <div className="price-amount">{'\u20B9'}499</div>
              <div className="price-period">per month {'\u00B7'} 1 child</div>
              <ul className="price-list">
                <li>{'\u2705'} Screenshots every 10 min</li>
                <li>{'\u2705'} Unlimited website history</li>
                <li>{'\u2705'} Screen time limits</li>
                <li>{'\u2705'} Bedtime control</li>
                <li>{'\u2705'} Stealth mode</li>
                <li>{'\u2705'} WhatsApp alerts</li>
                <li>{'\u2705'} Weekly reports</li>
                <li>{'\u2705'} Block websites</li>
              </ul>
              <button className="price-btn price-btn-primary" onClick={() => openSignup('Plus')}>Start 7-Day Free Trial {'\u2192'}</button>
            </div>
            {/* Family */}
            <div className="price-card">
              <h3>Family</h3>
              <div className="price-amount">{'\u20B9'}799</div>
              <div className="price-period">per month {'\u00B7'} up to 3 kids</div>
              <ul className="price-list">
                <li>{'\u2705'} Everything in Plus</li>
                <li>{'\u2705'} Screenshots every 5 min</li>
                <li>{'\u2705'} Up to 3 children</li>
                <li>{'\u2705'} PDF reports</li>
                <li>{'\u2705'} 90 days history</li>
                <li>{'\u2705'} Priority support</li>
              </ul>
              <button className="price-btn price-btn-outline" onClick={() => openSignup('Family')}>Start Free Trial {'\u2192'}</button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section id="faq" className="faq">
        <div className="container">
          <div className="faq-inner">
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div className="tag-pill">FAQ</div>
              <div className="bento-title">Questions? Answers.</div>
            </div>
            {faqItems.map((item, i) => (
              <div key={i} className={`faq-item${openFaq === i ? ' open' : ''}`}>
                <div className="faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {item.q}
                  <span className="faq-arrow">{'\u25BC'}</span>
                </div>
                <div className="faq-a">{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="final-cta">
        <div className="container">
          <div className="cta-card">
            <h2>Their safety starts with you</h2>
            <p>Join hundreds of Indian parents who protect their children's digital life with ParentsProtection.</p>
            <button className="btn-cta" onClick={() => openSignup('Plus')}>Start Your Free Trial {'\u2192'}</button>
            <div className="cta-sub">No credit card {'\u00B7'} No commitment {'\u00B7'} Cancel anytime</div>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="logo">{'\uD83D\uDEE1\uFE0F'} ParentsProtection</div>
              <p>Helping Indian parents keep their children safe in the digital world. Silent, powerful, and private.</p>
            </div>
            <div className="footer-col">
              <h4>Product</h4>
              <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>Features</a>
              <a href="#pricing" onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}>Pricing</a>
              <a href="#how" onClick={(e) => { e.preventDefault(); scrollToSection('how'); }}>How it works</a>
              <a href="#how" onClick={(e) => { e.preventDefault(); scrollToSection('how'); }}>Download</a>
            </div>
            <div className="footer-col">
              <h4>Company</h4>
              <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}>About Us</a>
              <a href="mailto:support@parentsprotection.org">Contact</a>
              <a href="mailto:support@parentsprotection.org">Support</a>
            </div>
            <div className="footer-col">
              <h4>Legal</h4>
              <a href="/privacy" onClick={(e) => { e.preventDefault(); alert('Privacy Policy page coming soon.'); }}>Privacy Policy</a>
              <a href="/terms" onClick={(e) => { e.preventDefault(); alert('Terms of Service page coming soon.'); }}>Terms of Service</a>
              <a href="/refund" onClick={(e) => { e.preventDefault(); alert('Refund Policy page coming soon.'); }}>Refund Policy</a>
            </div>
          </div>
          <div className="footer-bottom">
            <div>{'\u00A9'} 2026 ParentsProtection. Made with {'\u2764\uFE0F'} in India.</div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <a href="mailto:support@parentsprotection.org" style={{ color: '#94a3b8', textDecoration: 'none' }}>Contact Us</a>
            </div>
          </div>
        </div>
      </footer>

      {/* ═══ SIGNUP MODAL ═══ */}
      {showSignup && (
        <div className="signup-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowSignup(false); }}>
          <div className="signup-modal">
            <button className="signup-close" onClick={() => setShowSignup(false)}>{'\u00D7'}</button>

            {signupStep === 'form' ? (
              <>
                <div className="signup-header">
                  <h2>Create Your Account</h2>
                  <p>Start your free {selectedPlan} plan trial</p>
                </div>
                <div className="signup-body">
                  {errors && <div className="signup-error">{errors}</div>}

                  <div className="signup-field">
                    <label>Full Name</label>
                    <input
                      type="text"
                      placeholder="Your full name"
                      value={formData.name}
                      onChange={e => handleFormChange('name', e.target.value)}
                    />
                  </div>

                  <div className="signup-row">
                    <div className="signup-field">
                      <label>Email</label>
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={e => handleFormChange('email', e.target.value)}
                      />
                    </div>
                    <div className="signup-field">
                      <label>Phone (optional)</label>
                      <input
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={formData.phone}
                        onChange={e => handleFormChange('phone', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="signup-row">
                    <div className="signup-field">
                      <label>Password</label>
                      <input
                        type="password"
                        placeholder="Min 6 characters"
                        value={formData.password}
                        onChange={e => handleFormChange('password', e.target.value)}
                      />
                    </div>
                    <div className="signup-field">
                      <label>Confirm Password</label>
                      <input
                        type="password"
                        placeholder="Repeat password"
                        value={formData.password2}
                        onChange={e => handleFormChange('password2', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="signup-kids-header">
                    <span>Children</span>
                    <button className="btn-add-kid" onClick={addKid}>+ Add Child</button>
                  </div>

                  {kids.map((kid) => (
                    <div className="kid-row" key={kid.id}>
                      <div className="signup-field" style={{ marginBottom: 0 }}>
                        <label>Child Name</label>
                        <input
                          type="text"
                          placeholder="e.g. Aarav"
                          value={kid.kid_name}
                          onChange={e => updateKid(kid.id, 'kid_name', e.target.value)}
                        />
                      </div>
                      <div className="signup-field" style={{ marginBottom: 0 }}>
                        <label>Username</label>
                        <input
                          type="text"
                          placeholder="e.g. aarav2015"
                          value={kid.kid_username}
                          onChange={e => updateKid(kid.id, 'kid_username', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                          className={
                            usernameStatuses[kid.id] === 'available' ? 'input-ok' :
                            usernameStatuses[kid.id] === 'taken' ? 'input-error' : ''
                          }
                        />
                        {usernameStatuses[kid.id] === 'checking' && <div className="field-hint">Checking...</div>}
                        {usernameStatuses[kid.id] === 'available' && <div className="field-ok">@{kid.kid_username} is available</div>}
                        {usernameStatuses[kid.id] === 'taken' && <div className="field-error">@{kid.kid_username} is taken</div>}
                      </div>
                      <button
                        className="btn-remove"
                        onClick={() => removeKid(kid.id)}
                        style={{ visibility: kids.length > 1 ? 'visible' : 'hidden' }}
                      >{'\u00D7'}</button>
                    </div>
                  ))}

                  <button
                    className="signup-submit"
                    onClick={handleSignupSubmit}
                    disabled={submitting}
                    style={{ marginTop: '16px' }}
                  >
                    {submitting ? 'Creating account...' : 'Create Account & Start Trial'}
                  </button>
                </div>
              </>
            ) : (
              /* ── Success Screen ── */
              <div className="signup-success">
                <div className="success-icon">{'\u2705'}</div>
                <h2>Welcome to ParentsProtection!</h2>
                <p>Your account has been created. Check your email to verify, then install the app on your child's Mac.</p>

                <div className="kid-list">
                  {kids.map(kid => (
                    <div className="kid-list-item" key={kid.id}>
                      <span>{kid.kid_name}</span>
                      <span className="kid-username">@{kid.kid_username}</span>
                    </div>
                  ))}
                </div>

                <a
                  className="btn-download"
                  href="#"
                  onClick={e => e.preventDefault()}
                >
                  {'\u2B07\uFE0F'} Download for macOS
                </a>
                <br />
                <button
                  className="btn-dashboard"
                  onClick={() => { setShowSignup(false); navigate('/dashboard'); }}
                >
                  Go to Dashboard {'\u2192'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
